import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, Shield, ArrowRight, Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

export default function PaymentPage() {
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState("");
  const [bookingId, setBookingId] = useState(searchParams.get("booking") || "");
  const [loading, setLoading] = useState(false);
  const [paymentType, setPaymentType] = useState("full");

  useEffect(() => {
    axios.get(`${API}/packages`).then((res) => setPackages(res.data)).catch(() => {});
  }, []);

  const selectedPackage = packages.find(p => p.id === selectedPkg);
  const depositAmount = selectedPackage ? (selectedPackage.amount * 0.30).toFixed(2) : 0;

  const handlePayment = async () => {
    if (!selectedPkg) {
      toast.error("Please select a package.");
      return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const endpoint = paymentType === "deposit" ? "/payments/deposit" : "/payments/checkout";
      const res = await axios.post(`${API}${endpoint}`, {
        booking_id: bookingId || "direct-payment",
        package_id: selectedPkg,
        origin_url: origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error("Payment initiation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="payment-page" className="pt-20">
      <section className="py-24 md:py-32 bg-luxury-black min-h-screen">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">Payment</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-6">
              Secure<br /><span className="gold-gradient-text">Checkout</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/60 font-body font-light text-base leading-relaxed">
              Select your service package and proceed to our secure payment portal powered by Stripe.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Package Selection */}
            <motion.div initial="hidden" animate="visible">
              <motion.div variants={fadeUp} custom={3} className="mb-6">
                <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Select Package *</label>
                <Select value={selectedPkg} onValueChange={setSelectedPkg}>
                  <SelectTrigger data-testid="payment-package-select" className="bg-white/5 border-white/10 text-white h-12 rounded-sm">
                    <SelectValue placeholder="Choose a package" />
                  </SelectTrigger>
                  <SelectContent className="bg-luxury-charcoal border-white/10">
                    {packages.map((pkg) => (
                      <SelectItem key={pkg.id} value={pkg.id} className="text-white/80 focus:bg-gold/10 focus:text-white">
                        {pkg.name} - ${pkg.amount.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </motion.div>

              <motion.div variants={fadeUp} custom={4} className="mb-8">
                <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Booking Reference</label>
                <Input
                  data-testid="payment-booking-id"
                  value={bookingId}
                  onChange={(e) => setBookingId(e.target.value)}
                  placeholder="Auto-filled from your booking"
                  className={`bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm focus:border-gold/50 ${bookingId ? "border-gold/30" : ""}`}
                  readOnly={!!searchParams.get("booking")}
                />
              </motion.div>

              {selectedPkg && (
                <motion.div variants={fadeUp} custom={5} className="glass-card p-6 mb-8">
                  <h3 className="text-white font-body text-sm font-medium mb-3">Order Summary</h3>
                  <div className="flex justify-between items-center mb-4 pb-4 border-b border-white/10">
                    <span className="text-white/60 font-body text-sm">{selectedPackage?.name}</span>
                    <span className="text-gold font-body font-medium">${selectedPackage?.amount.toLocaleString()}</span>
                  </div>
                  {/* Payment Type Toggle */}
                  <div className="flex gap-2 mb-4">
                    <button data-testid="pay-full" onClick={() => setPaymentType("full")} className={`flex-1 py-3 text-xs font-body font-medium transition-all ${paymentType === "full" ? "bg-gold text-black" : "border border-white/10 text-white/50 hover:border-white/20"}`}>Full Payment</button>
                    <button data-testid="pay-deposit" onClick={() => setPaymentType("deposit")} className={`flex-1 py-3 text-xs font-body font-medium transition-all ${paymentType === "deposit" ? "bg-gold text-black" : "border border-white/10 text-white/50 hover:border-white/20"}`}>30% Deposit</button>
                  </div>
                  {paymentType === "deposit" && (
                    <div className="bg-gold/5 border border-gold/20 p-3 mb-4 text-xs font-body">
                      <p className="text-gold font-medium mb-1">Installment Plan</p>
                      <p className="text-white/50">Deposit now: <span className="text-gold">${depositAmount}</span></p>
                      <p className="text-white/50">2 monthly installments of <span className="text-white/70">${((selectedPackage?.amount - depositAmount) / 2).toFixed(2)}</span></p>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-white font-body text-sm font-medium">{paymentType === "deposit" ? "Pay Now" : "Total"}</span>
                    <span className="text-gold font-body text-lg font-medium">${paymentType === "deposit" ? depositAmount : selectedPackage?.amount.toLocaleString()}</span>
                  </div>
                </motion.div>
              )}

              <motion.div variants={fadeUp} custom={6}>
                <button
                  data-testid="payment-submit"
                  onClick={handlePayment}
                  disabled={loading || !selectedPkg}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-4 hover:bg-gold-light transition-colors disabled:opacity-50"
                >
                  <CreditCard size={18} />
                  {loading ? "Processing..." : "Proceed to Secure Checkout"}
                  <ArrowRight size={16} />
                </button>
              </motion.div>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div initial="hidden" animate="visible" className="space-y-6">
              <motion.div variants={fadeUp} custom={7} className="glass-card p-8">
                <Shield size={28} className="text-gold mb-4" />
                <h3 className="text-white font-heading text-xl mb-3">Secure Payment</h3>
                <p className="text-white/50 font-body font-light text-sm leading-relaxed">
                  All transactions are processed through Stripe's enterprise-grade security infrastructure with 256-bit SSL encryption.
                </p>
              </motion.div>
              <motion.div variants={fadeUp} custom={8} className="glass-card p-8">
                <h3 className="text-white font-body text-sm font-medium mb-4">What's Included</h3>
                <ul className="space-y-3">
                  {["Secure Stripe checkout", "Email confirmation", "Invoice & receipt", "Dedicated support", "Satisfaction guarantee"].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-white/60 text-sm font-body font-light">
                      <Check size={14} className="text-gold flex-shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
